





















































import BaseLoading from '@/components/base/BaseLoading.vue';
import BasePaginatorHoc from '@/components/base/BasePaginatorHoc.vue';
import ContentWrapper from '@/components/ContentWrapper.vue';
import SiteWrapper from '@/components/SiteWrapper.vue';
import { CourseAttributeSortColumns } from '@/jbi-shared/types/jtms.types';
import { SortOrder } from '@/jbi-shared/types/search.types';
import { Vue, Component, Watch } from 'vue-property-decorator';
import { Action, State } from 'vuex-class';
import {
  ApiState,
  PaginatorSpecs,
  SortEventData
} from '@/store/types/general.types';
import { RootState } from '@/store/store';
import { Pagination } from '@/store/modules/admin/types/admin.types';
import { FilteredGroupLevelAttributesPayload } from '@/store/modules/admin/types/group-level-attribute.types';
import {
  GroupLevelAttribute,
  GroupLevelAttributeType
} from '@/jbi-shared/types/jaas-group-level-attributes.types';
import CourseAttributeList from './component/CourseAttributeList.vue';
import { Debounce } from '@/jbi-shared/util/debounce.vue-decorator';
import CreateNewGroupLevelAttributeModal from '@/views/AdminGroupSettings/components/GroupLevelAttributes/CreateNewGroupLevelAttributeModal.vue';
import { clone } from '@/jbi-shared/util/group-level-attributes.util';
import EditAttributeModal from '@/views/AdminGroupManagement/components/EditAttributeModal.vue';
import DeleteCourseAttributeModal from './DeleteCourseAttributeModal.vue';

@Component({
  components: {
    BaseLoading,
    SiteWrapper,
    ContentWrapper,
    BasePaginatorHoc,
    CourseAttributeList
  }
})
export default class JTMSCourseAttribute extends Vue {
  attributeLabel: string = '';
  queryParamsForCourseAttributes: FilteredGroupLevelAttributesPayload = {
    attributeLabel: this.attributeLabel,
    limit: 50,
    page: 1,
    sortColumn: CourseAttributeSortColumns.ATTRIBUTE_LABEL,
    sortOrder: SortOrder.ASC
  };

  availableAttributeTypes: GroupLevelAttributeType[] = [];

  @Action('admin/getPaginatedGroupLevelAttributes')
  getPaginatedGroupLevelAttributes!: (
    payload: FilteredGroupLevelAttributesPayload
  ) => Promise<Pagination<GroupLevelAttribute>>;

  @State((state: RootState) => state.admin.paginatedGroupLevelAttributes)
  groupLevelAttributes!: Pagination<GroupLevelAttribute>;

  @State(
    (state: RootState) => state.admin.apiState.getPaginatedGroupLevelAttributes
  )
  getPaginatedGroupLevelAttributeApiState!: ApiState;

  @Action('admin/getGroupLevelAttributeTypes')
  getGroupLevelAttributeTypes!: () => void;

  @State(({ admin }: RootState) => admin.apiState.getGroupLevelAttributeTypes)
  getGroupLevelAttributeTypesApiState!: ApiState;

  @State((state: RootState) => state.admin.groupLevelAttributeTypes)
  groupLevelAttributeTypes!: GroupLevelAttributeType[];

  get isLoading() {
    return this.getPaginatedGroupLevelAttributeApiState.loading;
  }

  get items() {
    if (this.groupLevelAttributes) {
      return this.groupLevelAttributes.items;
    }

    return [];
  }

  get totalCount() {
    if (this.groupLevelAttributes) {
      return this.groupLevelAttributes.totalItems;
    }

    return 0;
  }

  get CourseAttributeList() {
    return CourseAttributeList;
  }

  created() {
    this.getPaginatedGroupLevelAttributes(this.queryParamsForCourseAttributes);

    this.getGroupLevelAttributeTypes();
  }

  openCreateNewCourseAttributeModal() {
    this.$buefy.modal.open({
      parent: this,
      component: CreateNewGroupLevelAttributeModal,
      hasModalCard: true,
      trapFocus: true,
      props: {
        allAttributeTypes: this.availableAttributeTypes,
        isCourseContext: true,
        showRequiredToggle: false
      },
      events: {
        addNewAttribute: () => {
          this.refreshList();
        }
      }
    });
  }

  openDeleteCourseLicenseTemplateModal(
    courseAttributeToDelete: GroupLevelAttribute
  ) {
    this.$buefy.modal.open({
      parent: this,
      component: DeleteCourseAttributeModal,
      hasModalCard: true,
      trapFocus: true,
      props: {
        courseAttribute: courseAttributeToDelete
      },
      events: {
        refreshPage: () => {
          this.refreshList();
        }
      }
    });
  }

  openUpdateCourseLicenseTemplateModal(attributeToEdit: GroupLevelAttribute) {
    this.$buefy.modal.open({
      parent: this,
      component: EditAttributeModal,
      hasModalCard: true,
      trapFocus: true,
      props: {
        attributeId: Number(attributeToEdit.id)
      },
      events: {
        attributeUpdateSuccess: () => {
          this.refreshList();
        }
      }
    });
  }

  refreshList() {
    this.getPaginatedGroupLevelAttributes(this.queryParamsForCourseAttributes);
  }

  modifyQueryParams<T>(paramsMap: Map<string, T>) {
    for (const [key, value] of paramsMap.entries()) {
      if (key in this.queryParamsForCourseAttributes) {
        // Use type assertion to safely assign the value
        (this.queryParamsForCourseAttributes as Record<string, any>)[
          key
        ] = value;
      }
    }
  }

  @Debounce(500)
  handleFilter() {
    const map = new Map();
    map.set('page', 1);
    map.set('attributeLabel', this.attributeLabel);

    this.modifyQueryParams(map);
    this.refreshList();
  }

  handleResetFilter() {
    this.attributeLabel = '';
  }

  handlePaginator(paginator: PaginatorSpecs) {
    const map = new Map();
    map.set('limit', paginator.perPage);
    map.set('page', paginator.page);

    this.modifyQueryParams(map);
    this.refreshList();
  }

  handleSort(data: SortEventData) {
    const map = new Map();
    map.set('sortColumn', data.sortColumn);
    map.set('sortOrder', data.sortOrder);

    this.modifyQueryParams(map);
    this.refreshList();
  }

  @Watch('getPaginatedGroupLevelAttributeApiState', { deep: true })
  onPaginatedGroupLevelAttributeStateChanged(state: ApiState) {
    if (state.success) {
      // Use $nextTick to wait for the DOM to update after the route change
      // This ensures the input element is fully rendered before we try to focus it
      this.$nextTick(() => {
        // Get the Buefy input component using the template ref
        const searchInput = this.$refs.searchInput as Vue;
        // Navigate the Buefy component structure to find the actual input element
        if (searchInput?.$el) {
          // Focus the input element after all updates are complete
          searchInput.$el.querySelector('input')!.focus();
        }
      });
    }
  }

  @Watch('getGroupLevelAttributeTypesApiState', { deep: true })
  assignCourseTypesToLocalVariable(state: ApiState) {
    if (state.success) {
      this.availableAttributeTypes = clone(
        this.groupLevelAttributeTypes
      ) as GroupLevelAttributeType[];
    }
  }
}
